import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { Address, Industry, OrganizationType, QualificationLevel, SalutationType } from 'data-model';

export type OrganizationFormGroup = FormGroup<{
  name: FormControl<string>;
  street: FormControl<string>;
  streetNumber: FormControl<string>;
  zipCode: FormControl<string>;
  city: FormControl<string>;
  countryCode: FormControl<string>;
  phone: FormControl<string>;
  [key: string]: AbstractControl;
}>;
export type OrganizationFurtherDataFormGroup = FormGroup<{
  industry: FormControl<Industry | null>;
  gln: FormControl<string>;
  glnNotAvailable: FormControl<boolean>;
  taxNumber: FormControl<string | null>;
  salesTaxId: FormControl<string | null>;
  salesTaxNotAvailable: FormControl<boolean>;
  [key: string]: AbstractControl;
}>;
export type UserFormGroup = FormGroup<{
  salutation: FormControl<SalutationType | null>;
  firstname: FormControl<string>;
  lastname: FormControl<string>;
  email: FormControl<string>;
  [key: string]: AbstractControl;
}>;
export type UserFormWithAgbsGroup = FormGroup<{
  salutation: FormControl<SalutationType | null>;
  firstname: FormControl<string>;
  lastname: FormControl<string>;
  email: FormControl<string>;
  agbChecked: FormControl<boolean>;
  [key: string]: AbstractControl;
}>;

export interface OrganizationAndUserRegistrationDto {
  user: UserRegistrationDto;
  organization: OrganizationRegistrationDto;
  captchaToken: string;
  termsOfUseAccepted: boolean;
  privacyPolicyAcknowledged: boolean;
  originCartridge?: OriginCartridgeDto;

  [index: string]: unknown;
}

export interface CampaignOrganizationAndUserRegistrationDto
  extends Omit<OrganizationAndUserRegistrationDto, 'originCartridge'> {
  organization: Omit<OrganizationRegistrationDto, 'phone'> & {
    phone: string | null;
  };
  serviceConditionsAccepted: boolean;
}

export interface ExistingCustomerRegistrationDto {
  user: UserRegistrationDto;
  organization: ExistingOrganizationRegistrationDto;
  cartridgeBookingRequests: CartridgeBookingForRegistration[];
}

export interface OriginCartridgeDto {
  id: string;
}

export interface CartridgeBookingForRegistration {
  cartridgeId: string;
  cartridgeVariantId: string;
  platformBilling?: boolean;
  overwriteCentAmount?: number;
  bookingReference?: string;
}

export interface UserRegistrationDto {
  salutation: SalutationType;
  firstname: string;
  lastname: string;
  email: string;
  language: string;
  phone?: string;
  position?: string;
}

export interface OrganizationRegistrationDto {
  name: string;
  gln?: string;
  address: Address;
  phone: string;
  billingEmail?: string;
  billingLanguageIsoCode?: string;
  salesTaxId?: string;
  taxNumber: string | null;
  commercialRegisterId?: string;
  industry?: Industry;
}

export interface ExistingOrganizationRegistrationDto {
  name: string;
  gln?: string;
  address: Address;
  phone: string;
  billingEmail: string;
  billingLanguageIsoCode: string;
  salesTaxId?: string;
  taxNumber: string;
  commercialRegisterId: string;
  organizationType: OrganizationType;
  industry: Industry;
  masterKin?: string;
  furtherKins?: string[];
  crmId?: string;
  markantId?: string;
  organizationId: string;
}

export interface RegistrationDetailsResponseDto {
  ticketIsValid: boolean;
  passwordRequired: boolean;
}

export interface CompleteRegistrationRequestDto {
  password: string;
}

export interface PlatformRequestData {
  id: string;
  type: RequestType;
  platformRequestStatus: PlatformRequestStatus;
  user: UserContactDto;
  admins?: UserContactDto[];
  organization: PlatformRequestOrganization;
  originalOrganizationData: PlatformRequestOrganization;
  agent?: PlatformRequestAgent;
  createdBy?: string;
  lastModifiedBy?: string;
  createdAt?: Date;
  lastModifiedAt?: Date;
  version: number;
  comments: Comment[];
  campaignId?: string;
  leadContext?: string;
}

export interface PlatformRequestAgent {
  id: string;
  firstname: string;
  lastname: string;
}

export interface PlatformRequestUserUpdateDto {
  salutation: string;
  firstname: string;
  lastname: string;
  email: string;
  phone?: string;
}

export interface Comment {
  author: string;
  text: string;
  createdAt: Date;
}

export interface Agent {
  id: string;
  firstname: string;
  lastname: string;
}

export interface UserContactDto {
  userId?: string;
  salutation: string;
  firstname: string;
  lastname: string;
  email: string;
  phone?: string;
  position?: string;
  language?: string;
  department?: string;
  targetRole?: string;
  invitedAt?: Date;
  platformTermsOfUseTimestamp?: string;
  platformPrivacyNoticeTimestamp?: string;
}

export interface PlatformRequestOrganization {
  organizationId: string;
  name: string;
  currentQualificationLevel?: QualificationLevel | null;
  targetQualificationLevel: QualificationLevel;
  gln: string | null;
  address: Address;
  commercialRegisterId?: string | null;
  salesTaxId?: string | null;
  taxNumber?: string | null;
  phone: string;
  organizationType: OrganizationType | null;
  industry: Industry | null;
  ezrCustomer: boolean | null;
  masterKin?: string;
  crmId?: string;
  markantId?: string | null;
}

export interface PlatformRequestsDto {
  pendingPlatformRequestsCount: number;
  inReviewPlatformRequestsCount: number;
}

export interface OrganizationAndUserRegistrationAuditDto {
  type?: 'REGISTRATION' | 'FURTHER_QUALIFICATION';
  registrationRequest: {
    id?: string;
    user: UserRegistrationAuditDto;
    organization: OrganizationRegistrationAuditDto;
    comment?: string;
    status: RegistrationAuditStatus;
    version: number;
  };
}

export interface UserRegistrationAuditDto {
  salutation: SalutationType;
  firstname: string;
  lastname: string;
  email: string;
  language: string;
  phone?: string;
  position?: string;
  termsOfUseVersion: string;
  termsOfUseTimestamp: string;
}

export interface OrganizationRegistrationAuditDto {
  name: string;
  gln?: string;
  address: Address;
  phone: string;
  salesTaxId?: string | null;
  taxNumber?: string | null;
  commercialRegisterId: string;
  organizationType?: OrganizationType;
  industry?: Industry;
  qualificationLevel: string;
  ezrCustomer?: boolean;
}

export type CampaignRegistrationStepperPage =
  | 'WELCOME'
  | 'ORGANIZATION_REGISTRATION_1'
  | 'ORGANIZATION_REGISTRATION_2'
  | 'USER_REGISTRATION_1'
  | 'BOOKING'
  | 'THANK_YOU';

export interface CampaignBookingUserData {
  salutation: SalutationType;
  firstname: string;
  lastname: string;
  email: string;
}

export interface CampaignBookingOrganizationData {
  name: string;
  street: string;
  streetNumber: string;
  zipCode: string;
  city: string;
  countryCode: string;
}

export enum RegistrationAuditStatus {
  BACKOFFICE_VERIFICATION_PENDING = 'BACKOFFICE_VERIFICATION_PENDING',
  BACKOFFICE_VERIFICATION_INQUIRY = 'BACKOFFICE_VERIFICATION_INQUIRY',
  BACKOFFICE_VERIFICATION_DECLINED = 'BACKOFFICE_VERIFICATION_DECLINED',
  BACKOFFICE_VERIFIED = 'BACKOFFICE_VERIFIED',
  COMPLETED = 'COMPLETED',
}

export enum PlatformRequestStatus {
  MAIL_VERIFICATION_PENDING = 'MAIL_VERIFICATION_PENDING',
  PENDING = 'PENDING',
  IN_REVIEW = 'IN_REVIEW',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export enum RequestType {
  FURTHER_QUALIFICATION_REQUEST = 'FurtherQualificationRequest',
  REGISTRATION_REQUEST = 'RegistrationRequest',
}

export interface BackofficeVerificationDto {
  nextRegistrationRequestStatus: PlatformRequestStatus;
  registrationRequest?: {
    organization: PlatformRequestOrganization;
  };
  version: number;
  comment: string;
}

export type Captcha = {
  isValid: boolean;
  token: string;
};

export type RegistrationStepperEvent = {
  type: 'next' | 'prev';
  current: string;
};
